import Vue from 'vue';
import Router from 'vue-router';

import ArticlesByTag from './pages/ArticlesByTag.vue';
import EventOverview from './pages/EventOverview.vue';
import EventArticle from './pages/EventArticle.vue';

import postIndex from './assets/posts.json';
import { normalizeAttributes, getRouteFromPost, MAIN_LANGUAGE } from './route.util';
import LanguageService from './services/LanguageService';
import DataSelector from './shared/DataSelector.vue';
import StandardEntry from './shared/StandardEntry.vue';

Vue.use(Router);

const LENGTH_CONTENT_TEASER = 60;
const CURRENT_LANGUAGE = LanguageService.getLanguage().code;
const isDateLowerThanNow = date => !date || (date && new Date(date).getTime() <= new Date().getTime());
const isCurrentLanguage = language => language === CURRENT_LANGUAGE || (!language && CURRENT_LANGUAGE === 'de');
const selectSectionAndLang = section => x =>
  x.attributes.section &&
  x.attributes.section.toLowerCase() === section.toLowerCase() &&
  isCurrentLanguage(x.attributes.language) &&
  isDateLowerThanNow(x.attributes.date);
const isNews = x => x.attributes.date && !x.attributes.section && !x.attributes.slug && x.attributes.categories.includes('news');
const sortDateDesc = (x, y) => (new Date(x.attributes.date) > new Date(y.attributes.date) ? -1 : 1);
const mapToStandardEntry = x => ({
  title: x.attributes.title,
  content: x.attributes.__content,
  created_at: x.attributes.date,
  author: x.attributes.author,
  path: x.path,
});
const CUSTOM_ROUTES = [
  {
    path: '/',
    component: DataSelector,
    props: {
      list: postIndex,
      selector: x => isNews(x) && isDateLowerThanNow(x.attributes.date) && isCurrentLanguage(x.attributes.language),
      mapper: mapToStandardEntry,
      sorter: sortDateDesc,
    },
  },
  {
    path: '/events',
    component: EventOverview,
  },
  {
    path: '/events/:title',
    component: EventArticle,
  },
  {
    path: '/joseki',
    component: DataSelector,
    props: {
      list: postIndex,
      selector: selectSectionAndLang('joseki'),
      mapper: mapToStandardEntry,
      sorter: sortDateDesc,
    },
  },
  {
    path: '/tesuji',
    component: DataSelector,
    props: {
      list: postIndex,
      selector: selectSectionAndLang('tesuji'),
      mapper: mapToStandardEntry,
      sorter: sortDateDesc,
    },
  },
  {
    path: '/tsume',
    component: DataSelector,
    props: {
      list: postIndex,
      selector: selectSectionAndLang('tsume'),
      mapper: mapToStandardEntry,
      sorter: sortDateDesc,
    },
  },
  {
    path: '/hisshi',
    component: DataSelector,
    props: {
      list: postIndex,
      selector: selectSectionAndLang('hisshi'),
      mapper: mapToStandardEntry,
      sorter: sortDateDesc,
    },
  },
  {
    path: '/tag/:tag',
    component: ArticlesByTag,
    props: {
      list: postIndex,
    },
  },
  {
    path: '/em2018',
    component: () => import(/* webpackChunkName: "em2018" */ './pages/Em2018.vue'),
  },
];

const mainRoutes = CUSTOM_ROUTES.reduce((acc, cur) => {
  const routes = LanguageService.getSupportedLanguages().map(x => {
    if (x.code === 'de') return cur;
    return {
      ...cur,
      path: `/${x.code}${cur.path}`,
    };
  });
  return [...acc, ...routes];
}, []);

const postRoutes = postIndex.map(item => {
  const { title, imageUrl, __content, date, author } = normalizeAttributes(item);
  const path = item.path.replace('src/posts/', '');

  return {
    path: '/',
    component: StandardEntry,
    props: {
      entry: { title, content: __content, created_at: date, author, imageUrl },
      content: `${__content.slice(0, LENGTH_CONTENT_TEASER)}...`,
      routerView: true,
    },
    children: [
      {
        path: getRouteFromPost(item),
        component: async () => (await import(`./posts/${path}`)).vue.component,
      },
    ],
  };
});

const router = new Router({
  mode: 'history',
  routes: [...mainRoutes, ...postRoutes],
});

router.beforeEach((to, from, next) => {
  // Redirect if fullPath begins with a hash (ignore hashes later in path)
  if (to.fullPath.substr(0, 2) === '/#') {
    const path = to.fullPath.substr(2);
    next(path);
    return;
  }
  // Redirect to language path if user revisits
  const langCode = LanguageService.getLanguage().code;
  if (to.fullPath.length < 2 && langCode !== MAIN_LANGUAGE) {
    next(`/${langCode}`);
    return;
  }
  next();
});

export default router;
